// SPDX-License-Identifier: AGPL-3.0-or-later

import * as AccessibilityCommands from '@app/features/accessibility/commands/AccessibilityCommands';
import Accessibility, {DMMessagePreviewMode} from '@app/features/accessibility/state/Accessibility';
import {SettingsTabSection} from '@app/features/app/components/dialogs/shared/SettingsTabLayout';
import {Switch} from '@app/features/ui/components/form/Switch';
import {Slider} from '@app/features/ui/components/Slider';
import type {RadioOption} from '@app/features/ui/radio_group/RadioGroup';
import {RadioGroup} from '@app/features/ui/radio_group/RadioGroup';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import styles from '@app/features/user/components/modals/tabs/accessibility_tab/VisualTab.module.css';
import type {I18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';

const dmMessagePreviewOptions = (i18n: I18n): ReadonlyArray<RadioOption<DMMessagePreviewMode>> => [
	{
		value: DMMessagePreviewMode.ALL,
		name: i18n._(msg`All messages`),
		desc: i18n._(msg`Show message previews for all DM conversations`),
	},
	{
		value: DMMessagePreviewMode.UNREAD_ONLY,
		name: i18n._(msg`Unread DMs only`),
		desc: i18n._(msg`Only show message previews for DMs with unread messages`),
	},
	{
		value: DMMessagePreviewMode.NONE,
		name: i18n._(msg`None`),
		desc: i18n._(msg`Don't show message previews in the DM list`),
	},
];
export const VisualTabContent: React.FC = observer(() => {
	const {i18n} = useLingui();
	const saturationFactor = Accessibility.saturationFactor;
	const alwaysUnderlineLinks = Accessibility.alwaysUnderlineLinks;
	const enableTextSelection = Accessibility.enableTextSelection;
	const mobileLayout = MobileLayout;
	return (
		<>
			<div className={styles.sliderSection}>
				<div className={styles.sliderHeader}>
					<label htmlFor="saturation" className={styles.sliderLabel}>
						<Trans>Saturation</Trans>
					</label>
					<p className={styles.sliderDescription}>
						<Trans>Adjust the saturation of all theme colors.</Trans>
					</p>
				</div>
				<Slider
					defaultValue={saturationFactor * 100}
					factoryDefaultValue={100}
					minValue={0}
					maxValue={100}
					step={1}
					markers={[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]}
					stickToMarkers={false}
					onMarkerRender={(value) => `${value}%`}
					onValueRender={(value) => <Trans>{value}%</Trans>}
					onValueChange={(value) => AccessibilityCommands.update({saturationFactor: value / 100})}
					showResetButton={true}
				/>
			</div>
			<Switch
				label={i18n._(msg`Always underline links`)}
				description={i18n._(msg`Make links to websites stand out more by always underlining them.`)}
				value={alwaysUnderlineLinks}
				onChange={(value) => AccessibilityCommands.update({alwaysUnderlineLinks: value})}
			/>
			<SettingsTabSection
				title={<Trans>Interaction</Trans>}
				description={<Trans>Adjust how you interact with the app</Trans>}
			>
				<Switch
					label={i18n._(msg`Enable text selection`)}
					description={
						mobileLayout.enabled
							? i18n._(
									msg`Allow selecting all text content in the app. This setting is disabled on mobile to prevent interference with touch interactions.`,
								)
							: i18n._(msg`Allow selecting all text content in the app.`)
					}
					value={enableTextSelection}
					disabled={mobileLayout.enabled}
					onChange={(value) => AccessibilityCommands.update({enableTextSelection: value})}
				/>
			</SettingsTabSection>
			<SettingsTabSection
				title={<Trans>DM Message Previews</Trans>}
				description={<Trans>Control when message previews are shown in the DM list</Trans>}
			>
				<RadioGroup
					options={dmMessagePreviewOptions(i18n)}
					value={Accessibility.dmMessagePreviewMode}
					onChange={(value) => AccessibilityCommands.update({dmMessagePreviewMode: value})}
					aria-label={i18n._(msg`DM message preview mode`)}
				/>
			</SettingsTabSection>
		</>
	);
});
